import React, { useEffect, useMemo, useState } from "react";
import styles from "../subscription/Subscription.module.css";
import { getSubscriptionDetails } from "../../api/HttpApis";
import Pagination from "../pagination/Pagination";
import { formatDate } from "../../utils/dateFormatter";
import InputText from "../../component/inputText/InputText";
import { debounce } from "../../utils/debounce";

// A bare "0" or "9.99" in an amount column reads ambiguously (0 what? is
// this a real charge or a free row?) — format as currency, and call out
// free rows explicitly instead of a bare "0".
const fmtAmount = (amount, currency = "USD") => {
  const value = Number(amount) || 0;
  if (value === 0) return "Free";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(value);
  } catch {
    return `$${value.toFixed(2)}`;
  }
};

// Matches the exact, case-sensitive Subscription.platform values the
// backend's /admin/subscriptions/details endpoint filters against.
// "Web" was dropped: it's not a distinct platform in the backend — purchase()
// treats platform==='stripe' and platform==='web' as the same flow (same
// cross-platform guard, same handling) everywhere, so it never meant
// anything different to filter by.
const PLATFORM_FILTER_OPTIONS = [
  { label: "All platforms", value: "" },
  { label: "Stripe", value: "stripe" },
  { label: "Google Play", value: "google_play" },
  { label: "Roku", value: "roku" },
  { label: "Fire TV", value: "fire_tv" },
  { label: "Amazon", value: "amazon" },
  { label: "Free", value: "free" },
  { label: "Admin-granted", value: "admin" },
];

const STATUS_FILTER_OPTIONS = [
  { label: "All statuses", value: "" },
  { label: "Active", value: "active" },
  { label: "Trialing", value: "trialing" },
  { label: "Inactive", value: "inactive" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Expired", value: "expired" },
  { label: "Refunded", value: "refunded" },
  { label: "Past due", value: "past_due" },
];

function SubscriptionList() {
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [platform, setPlatform] = useState("");
  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value) => {
        setSearch(value);
        setCurrentPage(1);
      }, 400),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [debouncedSetSearch]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSetSearch(value);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getSubscriptionDetails({
          page: currentPage,
          limit: rowsPerPage,
          search,
          status,
          platform,
        });
        setData(response.data || []);
        setTotalCount(response.total || 0);
      } catch (error) {
        console.error("Error fetching subscriptions:", error);
      }
    };

    fetchData();
  }, [currentPage, rowsPerPage, search, status, platform]);

  return (
    <div className={styles["tab-content"]}>
      <div className={styles["plans-toolbar"]}>
        <div>
          <h2 className={styles["table-title"]}>Active Subscriptions</h2>
          <p className={styles["table-subtitle"]}>
            Manage all subscriptions — search by user, or filter by status/platform
          </p>
        </div>

        <div className={styles["plans-toolbar-right"]}>
          <div className={styles["toolbar-search"]}>
            <InputText
              value={searchInput}
              onChange={handleSearchChange}
              placeholder="Search by email or receipt ID"
              className={styles["input-dark"]}
            />
          </div>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setCurrentPage(1);
            }}
            className={styles["toolbar-filter-select"]}
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            value={platform}
            onChange={(e) => {
              setPlatform(e.target.value);
              setCurrentPage(1);
            }}
            className={styles["toolbar-filter-select"]}
          >
            {PLATFORM_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <table className={styles["subscription-table"]}>
        <thead>
          <tr>
            <th>Platform</th>
            <th>User</th>
            {/* <th>User ID</th> */}
            <th>Plan</th>
            <th>Status</th>
            <th>Amount</th>
            <th>Start Date</th>
            <th>End Date</th>
               {/* <th>Actions</th> */}
          </tr>
        </thead>
        <tbody>
          {data?.length === 0 ? (
            <tr>
              <td colSpan="7" style={{ textAlign: "center" }}>
                No subscriptions found.
              </td>
            </tr>
          ) : (
            data?.map((item, index) => (
              <tr key={`${item.user_id}-${index}`}>
                <td>
                  {item.platform === "free" ? (
                    <span className={styles["platform-free"]}>Free (default tier)</span>
                  ) : (
                    item.platform
                  )}
                </td>
                <td>{item.user_name}</td>
                {/* <td>{item.user_id}</td> */}
                <td>{item.subscription_name}</td>
                <td>
                  <span className={`${styles.status} ${styles[item.status]}`}>
                    {item.status}
                  </span>
                </td>
                <td>{fmtAmount(item.amount, item.currency)}</td>
                <td>{formatDate(item.purchase_date)}</td>
                <td>{formatDate(item.expiration_date)}</td>
                    {/* <td>
                  {item.status === "active" && (
                    <button className={styles["cancel-btn"]}>Cancel</button>
                  )}
                </td> */}
              </tr>
            ))
          )}
        </tbody>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          setRowsPerPage={setRowsPerPage}
          totalCount={totalCount}
          dataLength={data?.length}
          columnsLength={7}
          className={styles["history-pagination"]}
        />
      </table>
    </div>
  );
}

export default SubscriptionList;